/**
 * Preview-environment event suppression (BAL-791).
 *
 * Vercel preview deployments are ephemeral and point at the throwaway e2e
 * Supabase branch, which is wiped and rebuilt on demand. Anything a preview
 * reports during a rebuild is an artifact of that rebuild, not a user-facing
 * defect: `permission denied for table feature_flags` (42501, Sentry
 * BALLEE-GK / BALLEE-GJ) fired from warm previews while the e2e branch was
 * mid-restore, 0 users affected, never reproducible in production.
 *
 * Rather than keep triaging preview-only noise, we drop it at the SDK edge.
 * This is a deliberate tradeoff: crashes that ONLY ever happen on a preview
 * deployment will no longer surface in Sentry. Production and staging
 * reporting are unaffected — the check is an exact match on the `preview`
 * value that Vercel sets in VERCEL_ENV / NEXT_PUBLIC_VERCEL_ENV, which is the
 * same source of truth `resolveEnvironment()` uses for the environment tag.
 *
 * VERCEL_ENV values: 'production' | 'preview' | 'development'.
 */
export const PREVIEW_ENVIRONMENT = 'preview';

/**
 * True when the resolved Sentry environment is a Vercel preview deployment.
 *
 * Exact match only — a substring match would also swallow a hypothetical
 * `production-preview`-style environment, and `staging`/`production` must
 * keep reporting.
 */
export function isPreviewEnvironment(environment: string | undefined): boolean {
  return environment === PREVIEW_ENVIRONMENT;
}
